import React, { useState, useCallback, useMemo } from 'react';
import { PetType } from '@spike/model';
import {
    Service,
    ServicePricing,
    FixedPriceOption,
    DaycareDurationOption,
    LatePickupFeeType,
    LATE_PICKUP_FEE_TYPES,
    DAYCARE_DEFAULTS
} from 'model/Service';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { buildPricingTaxesFromMarketplaces } from 'components/Service/Pricing/utils/taxElements';
import styles from './DaycarePricingStep.module.scss';
import Button from 'components/UI/V3/Button/Button';
import { DaycarePricingTypeSelector } from './Sections/PricingType/DaycarePricingTypeSelector';
import { ExpandableDurationOptions } from './Sections/Duration/ExpandableDurationOptions';
import { DaycareTaxesSelector } from './Sections/Taxes/DaycareTaxesSelector';

interface DaycarePricingStepProps {
    selectedPetTypes: Array<PetType>;
    marketplaces: Service['marketplaces'];
    existingService?: Service;
    onNext: (
        pricing: ServicePricing,
        daycareData: {
            durationOption: DaycareDurationOption;
            durationValue: string;
            latePickupFee: {
                enabled: boolean;
                type?: LatePickupFeeType;
                gracePeriod: number;
                fees: Record<string, number>;
            };
            selectedRooms?: Array<string>;
        },
        marketplaces: Service['marketplaces']
    ) => void;
}

export const DaycarePricingStep: React.FC<DaycarePricingStepProps> = ({
    selectedPetTypes,
    marketplaces: initialMarketplaces,
    existingService,
    onNext
}) => {
    const { organizationServicesInfo } = useOrganizationMarketplaces();

    const [pricingType, setPricingType] = useState<'fixed' | 'by_pet_size'>(() => {
        if (existingService?.pricing?.fixedPrice && existingService.pricing.fixedPrice.length > 0) {
            return 'fixed';
        }
        return 'fixed';
    });

    const [durationOption, setDurationOption] = useState<DaycareDurationOption | null>(() => {
        return existingService?.daycareData?.durationOption || null;
    });

    const [duration, setDuration] = useState(() => {
        return existingService?.daycareData?.durationValue || DAYCARE_DEFAULTS.DURATION;
    });

    const [latePickupFee, setLatePickupFee] = useState(() => {
        const existingLatePickupFee = existingService?.daycareData?.latePickupFee;
        return (
            existingLatePickupFee || {
                enabled: false,
                type: LATE_PICKUP_FEE_TYPES.PER_HOUR,
                gracePeriod: 15,
                fees: {} as Record<string, number>
            }
        );
    });
    const [taxable, setTaxable] = useState(
        () =>
            existingService?.pricing?.taxable ||
            initialMarketplaces.some(marketplace => marketplace.taxes.length > 0) ||
            false
    );
    const [marketplaces, setMarketplaces] = useState<Service['marketplaces']>(() => initialMarketplaces);
    const [fixedPrices, setFixedPrices] = useState<FixedPriceOption[]>(
        () => existingService?.pricing?.fixedPrice || []
    );
    const [latePickupFees, setLatePickupFees] = useState<Record<string, number>>(() => {
        return existingService?.daycareData?.latePickupFee?.fees || {};
    });

    // Keep marketplace list in sync when locations change in a previous step
    React.useEffect(() => {
        setMarketplaces(prev =>
            initialMarketplaces.map(marketplace => {
                const existing = prev.find(mp => mp.marketplaceId === marketplace.marketplaceId);
                return {
                    ...marketplace,
                    taxes: existing?.taxes ?? marketplace.taxes,
                    staffIds: existing?.staffIds ?? marketplace.staffIds,
                    roomIds: marketplace.roomIds ?? existing?.roomIds ?? [],
                    roomUnitIds: marketplace.roomUnitIds ?? existing?.roomUnitIds ?? [],
                    vehicleIds: marketplace.vehicleIds ?? existing?.vehicleIds ?? []
                };
            })
        );
    }, [initialMarketplaces]);

    // States for price validation vs deposit
    const [isPriceValidationValid, setIsPriceValidationValid] = useState(true);
    const [priceValidationErrors, setPriceValidationErrors] = useState<Record<string, string>>({});

    const taxes = useMemo(
        () => buildPricingTaxesFromMarketplaces(marketplaces, organizationServicesInfo),
        [marketplaces, organizationServicesInfo]
    );

    // Handler to receive validations from the price table
    const handlePriceValidationChange = useCallback((isValid: boolean, errors: Record<string, string>) => {
        setIsPriceValidationValid(isValid);
        setPriceValidationErrors(errors);
    }, []);

    const isValid = useMemo(() => {
        // 1. Validate that pricing type and duration are selected
        if (!pricingType || !durationOption) return false;

        // 2. Validate basic prices (set, including $0)
        if (pricingType === 'fixed') {
            const hasValidBasicPrices =
                fixedPrices.length > 0 &&
                fixedPrices.every(price => price.price !== undefined && price.price !== null && price.price >= 0);

            if (!hasValidBasicPrices) return false;

            // 3. Validate late pickup fees if enabled
            if (latePickupFee.enabled) {
                const selectedPetTypeIds = selectedPetTypes.map(pt => pt.id);
                const hasValidLatePickupFees = selectedPetTypeIds.every(petTypeId => {
                    const fee = latePickupFees[petTypeId];
                    return fee && fee > 0;
                });

                if (!hasValidLatePickupFees) return false;
            }
        }

        // 4. Validate taxes if taxable is enabled
        if (taxable && taxes.length === 0) return false;

        // 5. Validate deposit constraint (prices must not be lower than deposit)
        if (!isPriceValidationValid) return false;

        return true;
    }, [
        pricingType,
        durationOption,
        fixedPrices,
        latePickupFee,
        latePickupFees,
        selectedPetTypes,
        taxable,
        taxes,
        isPriceValidationValid
    ]);

    const handleNext = useCallback(() => {
        if (!isValid) return;

        const pricing: ServicePricing = {
            taxable,
            taxes,
            fixedPrice: pricingType === 'fixed' ? fixedPrices : [],
            variablePrice: pricingType === 'by_pet_size' ? undefined : undefined // TODO: Implement variable pricing
        };

        const daycareData = {
            durationOption: durationOption!,
            durationValue: duration,
            latePickupFee: {
                enabled: latePickupFee.enabled,
                type: latePickupFee.type,
                gracePeriod: latePickupFee.gracePeriod,
                fees: latePickupFees // Use fees from state instead of empty object
            },
            selectedRooms: [] // TODO: Implement rooms selection when needed
        };

        onNext(pricing, daycareData, marketplaces);
    }, [
        pricingType,
        taxable,
        taxes,
        fixedPrices,
        durationOption,
        duration,
        latePickupFee,
        latePickupFees,
        marketplaces,
        isValid,
        onNext
    ]);

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                {/* Pricing Type Selector */}
                <div className={styles.section}>
                    <DaycarePricingTypeSelector
                        value={pricingType}
                        onChange={setPricingType}
                    />
                </div>

                {/* Horizontal separator */}
                <div className={styles.separator}></div>

                {/* Expandable duration options */}
                <div className={styles.section}>
                    <ExpandableDurationOptions
                        pricingType={pricingType}
                        selectedDuration={durationOption}
                        duration={duration}
                        latePickupFee={latePickupFee}
                        fixedPrices={fixedPrices}
                        selectedPetTypes={selectedPetTypes}
                        latePickupFees={latePickupFees}
                        onDurationChange={setDurationOption}
                        onDurationValueChange={setDuration}
                        onLatePickupFeeChange={setLatePickupFee}
                        onFixedPricesChange={setFixedPrices}
                        onLatePickupFeesChange={setLatePickupFees}
                        onPriceValidationChange={handlePriceValidationChange}
                    />
                </div>

                {/* Taxable Service */}
                <div className={styles.section}>
                    <DaycareTaxesSelector
                        taxable={taxable}
                        marketplaces={marketplaces}
                        onTaxableChange={setTaxable}
                        onChangeMarketplaces={setMarketplaces}
                    />
                </div>
            </div>

            <div className={styles.buttonContainer}>
                <Button
                    label="Save"
                    variant="primary"
                    onClick={handleNext}
                    disabled={!isValid}
                    className={styles.saveButton}
                />
            </div>
        </div>
    );
};
